import {
    Grid,
    Tooltip,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/styles';
import {
    ClientMessagesStatusEnum,
    ClientMessagesTypeEnum,
    GridSettingsContext as IGridSettingsContext
} from '@tassat/common-definitions';
import { Information } from '@tassat/icon';
import { DisplayCard } from '@tassat/layout';
import { PoweredBySymbol } from '@tassat/symbols';
import { browserSessionStore, formatEpochDate } from '@tassat/utility';
import moment from 'moment';
import React, { useState } from 'react';
import MarkdownView from 'react-showdown';

import { getAssetBalancesTileData } from 'Lib/accountBalancesHelper';
import history from 'Lib/browserHistory';
import { exportClientAccountsData } from 'Lib/exports/walletExports';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAccountPositionsStats } from 'Selectors/accountPositions';
import { getAllSecurityCurrencyPairs } from 'Selectors/assets';
import { getClientAdvisoryMessage, getClientBannerMessage } from 'Selectors/clientMessages';
import { getClientFirm } from 'Selectors/firms';
import { getKillSwitchOn } from 'Selectors/killswitch';
import { getNetAssetValuesAsCurCode } from 'Selectors/netAssetValues';
import { getMappedUserAccountsByUserIdClient } from 'Selectors/userAccounts';
import AssetBalancesChart from 'SharedComponents/assetBalancesChart';
import AssetList from 'SharedComponents/assetList';
import { AdvisoryMessage } from 'SharedComponents/bannerAdvisoryMessage';
import { useClientLogos } from 'SharedComponents/hooks/useClientLogos';
import SideListLayout from 'SharedComponents/layouts/sideListLayout/';
import { GridSettingsContext } from 'SharedComponents/layouts/sideListLayout/gridSettingsContext';
import { SideListGridOptions } from 'SharedComponents/sideList';

import AccountBalances from './accountBalances';
import PendingTransactionsTable from './pendingTransactions';
import { columnDefs, walletSortOptions } from './walletsGridOptions';

import type { IWalletsDeashboardProps } from '.';

export function WalletsDashboard(props: IWalletsDeashboardProps) {
    const { classes } = props;

    const navPriceList = useAppSelector(getNetAssetValuesAsCurCode);
    const killSwitch = useAppSelector(getKillSwitchOn);
    const wallets = useAppSelector((state) => getMappedUserAccountsByUserIdClient(state, browserSessionStore.getItem('userid') ?? '', '', false, true));
    const firm = useAppSelector(getClientFirm);
    const assetPairs = useAppSelector(getAllSecurityCurrencyPairs);
    const accountBalancesStats = useAppSelector((state) => getAccountPositionsStats(state, true, firm.IsCollateral));
    const banner = useAppSelector((state) => getClientBannerMessage(state));
    const advisory = useAppSelector((state) => getClientAdvisoryMessage(state));
    const theme = useTheme<ApplicationMuiTheme>();
    const clientLogoUrls = useClientLogos();
    // Use the following state to handle Assets List Balance Tiles overlay
    const [ assetListOverlayOpen, setAssetListOverlayOpen ] = useState<boolean>(false);

    const assetListBalances = React.useMemo(() => {
        return accountBalancesStats && getAssetBalancesTileData(assetPairs, accountBalancesStats);
    }, [ accountBalancesStats, assetPairs ]);

    const layoutSettingsMemo: IGridSettingsContext = React.useMemo(() => {
        return {
            // The below are no longer taken from the Context in Side List
            columnDefs: [],
            options: { cellHeight: 0 }
        };
    }, []);

    const gridOptions: SideListGridOptions = React.useMemo(() => ({
        idField: 'userAccount.UserAccountId',
        cellHeight: 100,
        searchByColumn: 'account.AccountName',
        pinnedRowData: [
            {
                rowPinnedId: 'top-pinned-row',
                title: 'All Lynq Accounts',
                count: wallets.length
            }
        ],
        exportData: exportClientAccountsData,
        sortOptions: walletSortOptions
    }), [wallets.length]);

    const navPriceMap = React.useMemo(() => {
        if (!navPriceList) return {};

        return assetPairs.reduce((acc, pair) => {
        if (!pair.security?.AssetId || !pair.security?.Ticker) return acc;
        const entry = Object.values(navPriceList)
            .find(navPrice => navPrice?.AssetId === pair.security?.AssetId);
                    if (entry) acc[pair.security.Ticker] = entry;

            return acc;
        }, {} as Record<string, any>);
    }, [ navPriceList, assetPairs ]);

    return (
        <GridSettingsContext.Provider value={layoutSettingsMemo}>
            {advisory?.ClientMsgType == ClientMessagesTypeEnum.ADVISORY &&
                advisory?.Status == ClientMessagesStatusEnum.DISPLAYABLE ? <AdvisoryMessage /> : null}
            {assetListOverlayOpen ?
                <AssetList
                    assetListBalances={assetListBalances}
                    goBack={() => history.push('/accounts')}
                    navPrice={navPriceMap}
                />
                : null}
            {!assetListOverlayOpen ?
                <SideListLayout
                    columnDefs={columnDefs}
                    gridOptions={gridOptions}
                    rowData={wallets}
                >
                    <Grid
                        className={classes.dashboard}
                        container
                        direction='column'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <Grid
                            className={classes.dashboardHeaderContainer}
                            item
                            xs={12}
                        >
                            <Typography
                                className={classes.dashboardUpdatedText}
                                variant='body1'
                            >
                                <strong>{firm.Name} Client Dashboard</strong> {formatEpochDate(moment()
                                    .utc(), 'MMMM DD, YYYY')}
                                {/* <strong>{firm.Name} DASHBOARD</strong> {formatMomentDate(moment())} */}
                            </Typography>
                        </Grid>
                        {killSwitch &&
                            <Grid
                                container
                                direction='row'
                                justifyContent='center'
                                spacing={0}
                                wrap='nowrap'
                            >
                                <Typography
                                    className={classes.killSwitchMessage}
                                    variant='body1'
                                >Redeem and Deposit features are temporarily unavailable.
                                </Typography>
                            </Grid>}
                        {banner?.ClientMsgType === ClientMessagesTypeEnum.BANNER &&
                            banner?.Status === ClientMessagesStatusEnum.DISPLAYABLE && (
                            <DisplayCard
                                className={classes.tableContainer}
                                title='IMPORTANT MESSAGE'
                            >
                                <Grid
                                    container
                                    direction='row'
                                    justifyContent='center'
                                >
                                    <MarkdownView markdown={banner?.Message} />
                                </Grid>
                            </DisplayCard>
                        )}
                        <Grid
                            className={classes.tableContainer}
                            container
                            direction='row'
                            item
                            wrap='nowrap'
                            xs={12}
                        >
                            <Grid
                                className={classes.balancesContainer}
                                container
                                direction='column'
                                item
                                xs={8}
                            >
                                <AccountBalances setAssetListOverlayOpen={setAssetListOverlayOpen} />
                            </Grid>
                            <Grid
                                container
                                item
                                xs={4}
                            >
                                <AssetBalancesChart
                                    height={300}
                                    margin={{
                                        t: 28,
                                        b: 38,
                                        r: 40
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid
                            className={classes.tableContainer}
                            container
                            direction='column'
                            item
                            xs={12}
                        >
                            <PendingTransactionsTable transactionFilter='pending' />
                        </Grid>
                        <Grid
                            className={classes.tableContainer}
                            container
                            direction='column'
                            item
                            xs={12}
                        >
                            <PendingTransactionsTable transactionFilter='recent' />
                        </Grid>
                        <Grid
                            alignItems='flex-end'
                            className={classes.dashboardFooter}
                            container
                            justifyContent='space-between'
                        >
                            {theme?.app?.clientTheme.layoutSettings.useTassatPayLogoOnDashboard &&
                                theme?.app?.clientTheme.layoutSettings.usePoweredByImage ?
                                (
                                    <>
                                        <Grid
                                            className={classes.footerLogoContainer}
                                            container
                                            direction='row'
                                            item
                                        >
                                            <Typography>Operated by tZERO Securities, LLC</Typography>
                                            <img
                                                className={classes.footerLogo}
                                                src={clientLogoUrls?.ClientColorLogoUrl}
                                            />
                                            <Tooltip
                                                arrow
                                                placement='top'
                                                title={
                                                    <Typography>
                                                        Operated by tZERO Securities, LLC, a SEC registered broker-dealer and FINRA
                                                        member. For more information about tZERO Securities, LLC visit{' '}
                                                        <a
                                                            href='https://brokercheck.finra.org/'
                                                            rel='noreferrer'
                                                            target='_blank'
                                                        >
                                                            FINRA’s BrokerCheck
                                                        </a>
                                                        {' '}and click{' '}
                                                        <a
                                                            href='https://tzero.com/disclosure-library/'
                                                            rel='noreferrer'
                                                            target='_blank'
                                                        >
                                                            here
                                                        </a>
                                                        {' '}for more information
                                                    </Typography>
                                                }
                                            >
                                                {/* Div needed for tooltip ref */}
                                                <div className={classes.tzeroInfoIconContainer}>
                                                    <Information
                                                        className={classes.tzeroInfoIcon}
                                                        iconColor='#4A4C50'
                                                    />
                                                </div>
                                            </Tooltip>
                                        </Grid>
                                        <Grid
                                            className={classes.footerLogoContainer}
                                            container
                                            direction='row'
                                            item
                                        >
                                            <Grid
                                                className={classes.centerLogo}
                                                container
                                                item
                                            >
                                                <Typography>Partnered with</Typography>
                                                <img
                                                    className={classes.footerLogo}
                                                    src='https://storage.googleapis.com/lynq-svgs/arca/svgs/Arca_Horizontal_Color_Logo.png'
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            className={classes.footerLogoContainer}
                                            item
                                        >
                                            <PoweredBySymbol
                                                isAdmin={false}
                                                orientation='horizontal'
                                            />
                                        </Grid>
                                    </>
                                )
                                : (
                                    <Grid
                                        item
                                        sm={12}
                                    >
                                        <Typography>Operated by</Typography>
                                        <img
                                            className={classes.footerLogo}
                                            src={clientLogoUrls?.ClientColorLogoUrl}
                                        />
                                        <Typography>
                                            Click&nbsp;
                                            <a
                                                href='https://tzero.com/'
                                                rel='noreferrer'
                                                target='_blank'
                                            >
                                                here
                                            </a>
                                            &nbsp;for more information.
                                        </Typography>
                                    </Grid>
                                )}
                        </Grid>
                    </Grid>
                </SideListLayout>
                : null}
        </GridSettingsContext.Provider>
    );
}